// Design tokens — theme presets (P0 of the Design Import System, 2026-07-08).
// Frontend-only: the token *values* live here in previewUI, not on the backend.
// A profile stores only the chosen preset ids (design.themeTokens), and this
// module turns those ids into CSS custom properties (--ul-*). Blocks consume
// them via `var(--ul-x, <block-default>)`, so a profile on the 'default' preset
// (or one that stored nothing) renders byte-identically to before.
//
// Same emit rule as buildLiveTokenVars in semanticColors.js: only emit a var
// when the preset actually sets it; a missing var falls through to the
// consumer's own hard-coded default. Kept portable — no external deps, no Redux.

// -------------------------------------------------------------------------
// Typography scale — per-role text style (display / h1 / h2 / body / label).
// Each role maps to CSS vars: --ul-<role>-size / -weight / -tracking / -lh /
// -transform. 'default' mirrors the current hard-coded Heading values
// (h1 = 22px/bold, body = 15px) so nothing changes until a preset is picked.
export const TYPOGRAPHY_SCALES = {
  default: {
    label: 'Default',
    roles: {
      display: { size: '34px', weight: '700', tracking: 'normal', lineHeight: '1.2', transform: 'none' },
      h1: { size: '22px', weight: '700', tracking: 'normal', lineHeight: '1.3', transform: 'none' },
      h2: { size: '18px', weight: '600', tracking: 'normal', lineHeight: '1.35', transform: 'none' },
      body: { size: '15px', weight: '400', tracking: 'normal', lineHeight: '1.5', transform: 'none' },
      label: { size: '12px', weight: '600', tracking: 'normal', lineHeight: '1.4', transform: 'none' },
    },
  },
  // Bold, tight, uppercase-label look — the "Doc Serwis" landing family:
  // heavy display headings + an eyebrow label in caps.
  boldDisplay: {
    label: 'Bold display',
    roles: {
      display: { size: '44px', weight: '800', tracking: '-0.02em', lineHeight: '1.05', transform: 'none' },
      h1: { size: '30px', weight: '800', tracking: '-0.01em', lineHeight: '1.15', transform: 'none' },
      h2: { size: '20px', weight: '700', tracking: 'normal', lineHeight: '1.25', transform: 'none' },
      body: { size: '15px', weight: '400', tracking: 'normal', lineHeight: '1.6', transform: 'none' },
      label: { size: '12px', weight: '700', tracking: '0.14em', lineHeight: '1.4', transform: 'uppercase' },
    },
  },
  // Softer, larger body, serif-friendly rhythm — long-form / personal pages.
  editorial: {
    label: 'Editorial',
    roles: {
      display: { size: '36px', weight: '600', tracking: 'normal', lineHeight: '1.2', transform: 'none' },
      h1: { size: '24px', weight: '600', tracking: 'normal', lineHeight: '1.35', transform: 'none' },
      h2: { size: '19px', weight: '500', tracking: 'normal', lineHeight: '1.4', transform: 'none' },
      body: { size: '16px', weight: '400', tracking: 'normal', lineHeight: '1.7', transform: 'none' },
      label: { size: '13px', weight: '500', tracking: '0.02em', lineHeight: '1.4', transform: 'none' },
    },
  },
};

// -------------------------------------------------------------------------
// Density scale — the block-spacing rhythm (--ul-space-*). 'comfortable'
// mirrors the current 4/8/12/16 rhythm used across previewUI.
export const DENSITY_SCALES = {
  comfortable: { label: 'Comfortable', xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' },
  compact: { label: 'Compact', xs: '2px', sm: '6px', md: '10px', lg: '14px', xl: '20px' },
  spacious: { label: 'Spacious', xs: '6px', sm: '12px', md: '18px', lg: '28px', xl: '40px' },
};

// -------------------------------------------------------------------------
// Radius scale — corner rounding (--ul-radius-*). Vocabulary + values mirror
// the per-block Shape control (SHAPE_DATA: none 0 / small 4 / medium 8 /
// large 16 / round 36) so the Theme-style corners and the block Shape editor
// speak the same language. Each option applies one radius to blocks/items/
// buttons uniformly (sm = md = lg).
export const RADIUS_SCALES = {
  none: { label: 'None', sm: '0px', md: '0px', lg: '0px' },
  small: { label: 'Small', sm: '4px', md: '4px', lg: '4px' },
  medium: { label: 'Medium', sm: '8px', md: '8px', lg: '8px' },
  large: { label: 'Large', sm: '16px', md: '16px', lg: '16px' },
  round: { label: 'Round', sm: '36px', md: '36px', lg: '36px' },
};

// -------------------------------------------------------------------------
// Shadow scale — elevation (--ul-shadow-*). Vocabulary + values mirror the
// per-block Shadow control (None / Subtle / Soft / Hard). 'none' emits nothing.
export const SHADOW_SCALES = {
  none: { label: 'None', sm: '', md: '', lg: '' },
  subtle: {
    label: 'Subtle',
    sm: 'rgb(0 0 0 / 6%) 0 2px 8px 0',
    md: 'rgb(0 0 0 / 6%) 0 2px 8px 0',
    lg: 'rgb(0 0 0 / 6%) 0 2px 8px 0',
  },
  soft: {
    label: 'Soft',
    sm: 'rgb(0 0 0 / 12%) 0 4px 14px 0',
    md: 'rgb(0 0 0 / 12%) 0 4px 14px 0',
    lg: 'rgb(0 0 0 / 12%) 0 4px 14px 0',
  },
  hard: { label: 'Hard', sm: 'rgb(0 0 0) 3px 4px 0 0', md: 'rgb(0 0 0) 3px 4px 0 0', lg: 'rgb(0 0 0) 3px 4px 0 0' },
};

// Defaults that reproduce the current look exactly — used when a profile has
// no themeTokens yet, and as the fallback for an unknown/missing preset id.
// radius 'medium' (8px) == the current BlockContainer base radius.
export const DEFAULT_THEME_TOKENS = {
  typographyScale: 'default',
  density: 'comfortable',
  radius: 'medium',
  shadow: 'none',
};

// Push "key: value;" only when value is truthy, so an empty preset field
// (e.g. shadow 'none') emits no var and the consumer keeps its own default.
const emit = (out, cssVar, value) => {
  if (value) out.push(`${cssVar}: ${value};`);
};

// Turn the chosen preset ids into a CSS custom-property block (string).
// Returns '' when nothing resolves, so DesignSite injects nothing.
export const buildThemeTokenVars = (themeTokens) => {
  if (!themeTokens) return '';
  const out = [];

  const typography = TYPOGRAPHY_SCALES[themeTokens.typographyScale];
  if (typography) {
    Object.entries(typography.roles).forEach(([role, style]) => {
      emit(out, `--ul-${role}-size`, style.size);
      emit(out, `--ul-${role}-weight`, style.weight);
      emit(out, `--ul-${role}-tracking`, style.tracking);
      emit(out, `--ul-${role}-lh`, style.lineHeight);
      emit(out, `--ul-${role}-transform`, style.transform);
    });
  }

  const density = DENSITY_SCALES[themeTokens.density];
  if (density) {
    ['xs', 'sm', 'md', 'lg', 'xl'].forEach((step) => emit(out, `--ul-space-${step}`, density[step]));
  }

  const radius = RADIUS_SCALES[themeTokens.radius];
  if (radius) {
    ['sm', 'md', 'lg'].forEach((step) => emit(out, `--ul-radius-${step}`, radius[step]));
  }

  const shadow = SHADOW_SCALES[themeTokens.shadow];
  if (shadow) {
    ['sm', 'md', 'lg'].forEach((step) => emit(out, `--ul-shadow-${step}`, shadow[step]));
  }

  return out.join('\n');
};
